/**
 * A feed's Gym page. Four things, in the creator's language.
 *
 *   1. Is it on, and how much of the page is lent.
 *   2. What is being tested right now, and what this feed's readers have said about it.
 *   3. What finished, and what this feed contributed to each.
 *   4. Anything proven that this feed has not taken.
 *
 * 🔴 THE RULES THIS PAGE IS WRITTEN AGAINST, each from something the previous version did:
 *
 *   - No internal notes. v2's arm description was `feed_gym_policies.notes` — one column doing
 *     two jobs, a short label for most arms and 805 characters of design rationale for one, all
 *     rendered to creators.
 *   - No raw counters. "0 of 0 sittings" reads as a result when it is an absence.
 *   - No internal identifiers. Chart bands keyed `d14-v1`; an x-axis of bare epoch ids that
 *     rendered "1 … 0" like a metric dying.
 *   - No ratios of durations. "Testing on 0.0 of the last 3.3 days" answers a question nobody
 *     asked; the real one is "are my readers being experimented on, and how much".
 *   - No fleet mechanics. The 9/18 livelock wrote "We stopped a head-to-head early" into creators'
 *     history every hour — our own bug narrating itself to the one person who could do nothing
 *     about it.
 *
 * A real chart IS wanted, and this has one: named series, a date axis, and a legend.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";

import { adoptGymLayer, gymFeedQueryOptions, type GymExperimentView } from "@/lib/api";

export const Route = createFileRoute("/app/_sidebar-layout/feed/$feedId/gym")({
  component: RouteComponent,
  /**
   * See the public page for why this is required rather than optional: with SSR query integration,
   * a `useQuery` with no loader behind it never fires and the page shows its pending state for
   * ever. Swallowing the rejection keeps a 404 — someone else's feed — as the component's own
   * "cannot be loaded" state rather than a route-level error.
   */
  loader: ({ context: { queryClient }, params: { feedId } }) =>
    queryClient.ensureQueryData(gymFeedQueryOptions(Number(feedId))).catch(() => null),
});

function pct(value: number | null | undefined): string {
  return value === null || value === undefined ? "—" : `${Math.round(value * 100)}%`;
}

function when(value: string | null): string {
  return value
    ? new Date(value).toLocaleDateString(undefined, { month: "short", day: "numeric" })
    : "";
}

/**
 * What this feed's own readers said, in one sentence.
 *
 * 🔴 SAID IN VISITS, NOT IN COUNTS THAT READ AS RESULTS. Zero decisive visits is an absence and is
 * worded as one — the previous page printed "0 of 0" beside a verdict and creators read it as a
 * finding of nothing.
 */
function contribution(experiment: {
  yourDecisive: number;
  yourWins: number;
  yourSessions: number;
}): string {
  if (experiment.yourSessions === 0) {
    return "Your readers have not reached the tested slots yet.";
  }
  if (experiment.yourDecisive === 0) {
    return `${experiment.yourSessions} visit${experiment.yourSessions === 1 ? "" : "s"} so far, none where a reader engaged with one side more than the other.`;
  }
  const losses = experiment.yourDecisive - experiment.yourWins;
  return `Your readers preferred the tested layer in ${experiment.yourWins} of ${experiment.yourDecisive} visits, against ${losses} for your own ordering.`;
}

/** A small stacked bar chart: named series, a date axis, a legend. No library. */
function Chart({ rows }: { rows: { day: string; control: number; candidate: number }[] }) {
  if (rows.length === 0) return null;
  const tallest = Math.max(...rows.map((r) => r.control + r.candidate), 1);
  // `noUncheckedIndexedAccess` is on, and it is right to be: an empty array is returned by the API
  // whenever a feed has contributed nothing yet, which is the common case on a new feed.
  const first = rows[0];
  const last = rows[rows.length - 1];
  return (
    <figure className="flex flex-col gap-2">
      <div className="flex items-end gap-1 h-32" role="img" aria-label="Decisive visits per day">
        {rows.map((row) => {
          const total = row.control + row.candidate;
          return (
            <div key={row.day} className="flex flex-col justify-end flex-1 min-w-2 h-full">
              <div
                className="bg-iris-9"
                style={{ height: `${(row.candidate / tallest) * 100}%` }}
                title={`${row.candidate} preferred the tested layer`}
              />
              <div
                className="bg-slate-8"
                style={{ height: `${(row.control / tallest) * 100}%` }}
                title={`${row.control} preferred your ordering`}
              />
              <span className="sr-only">
                {when(row.day)}: {total} decisive visits
              </span>
            </div>
          );
        })}
      </div>
      <div className="flex justify-between text-slate-dim text-xs">
        <span>{when(first?.day ?? null)}</span>
        <span>{when(last?.day ?? null)}</span>
      </div>
      <figcaption className="flex gap-4 text-xs text-slate-dim">
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 bg-iris-9 inline-block" /> preferred the tested layer
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 bg-slate-8 inline-block" /> preferred your ordering
        </span>
      </figcaption>
    </figure>
  );
}

function Suggestion({
  feedId,
  experiment,
}: {
  feedId: number;
  experiment: GymExperimentView;
}) {
  const queryClient = useQueryClient();
  const [declined, setDeclined] = useState(false);
  const adopt = useMutation({
    mutationFn: () => adoptGymLayer(feedId, experiment.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["gym", "feed", feedId] }),
  });

  if (declined) return null;

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-slate-5 p-4">
      <div className="text-slate-normal">{experiment.name}</div>
      <p className="text-slate-dim text-sm">
        Readers preferred this in {pct(experiment.winRate)} of the visits where they engaged with
        one side or the other, across {experiment.feeds ?? 0} feeds.
      </p>
      {adopt.isError ? (
        <p className="text-red-11 text-sm">
          {/* The API's refusal text is creator-readable by design; shown verbatim. */}
          {(adopt.error as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
            "That layer could not be added just now."}
        </p>
      ) : null}
      <div className="flex gap-2">
        <button
          type="button"
          className="px-3 py-1.5 rounded-md bg-iris-9 text-white text-sm disabled:opacity-60"
          disabled={adopt.isPending || adopt.isSuccess}
          onClick={() => adopt.mutate()}
        >
          {adopt.isSuccess ? "Added" : adopt.isPending ? "Adding…" : "Add at 25%"}
        </button>
        <button
          type="button"
          className="px-3 py-1.5 rounded-md border border-slate-8 text-slate-dim text-sm"
          onClick={() => setDeclined(true)}
        >
          No thanks
        </button>
      </div>
    </div>
  );
}

function RouteComponent() {
  const { feedId } = Route.useParams();
  const id = Number(feedId);
  const { data, isPending, isError } = useQuery(gymFeedQueryOptions(id));

  if (isPending) {
    return <Shell>Loading…</Shell>;
  }
  if (isError || !data) {
    return <Shell>This page cannot be loaded right now.</Shell>;
  }

  // Not enrolled. The layer IS the switch, so the only thing to say is where the switch lives.
  if (!data.layer) {
    return (
      <Shell>
        <h1 className="text-2xl text-slate-normal">Feed Gym</h1>
        <p className="text-slate-dim max-w-prose">
          This feed is not taking part. Add a Gym layer in the editor to lend a share of every page
          to Graze&rsquo;s feed research — and see what your readers make of the ideas being tested.
        </p>
        <Link
          to="/app/feed/$feedId"
          params={{ feedId }}
          className="text-iris-11 underline w-fit"
        >
          Open the editor
        </Link>
        <p className="text-slate-dim text-sm">
          You can see what the research has found so far at{" "}
          <Link to="/gym" className="text-iris-11 underline">
            graze.social/gym
          </Link>
          .
        </p>
      </Shell>
    );
  }

  return (
    <Shell>
      <section className="flex flex-col gap-2">
        <h1 className="text-2xl text-slate-normal">Feed Gym</h1>
        <p className="text-slate-dim">
          {data.layer.paused
            ? "Your Gym layer is set to 0%, so nothing is being tested on this feed."
            : `You are lending ${pct(data.layer.share)} of every page to Graze's feed research.`}
        </p>
        <Link
          to="/app/feed/$feedId"
          params={{ feedId }}
          className="text-iris-11 underline text-sm w-fit"
        >
          Change this in the editor
        </Link>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-lg text-slate-normal">Being tested now</h2>
        {data.now ? (
          <>
            <div className="text-slate-normal">{data.now.name}</div>
            <p className="text-slate-dim">{contribution(data.now)}</p>
            {data.now.winRate !== null ? (
              <p className="text-slate-dim text-sm">
                Across every feed taking part, it is winning {pct(data.now.winRate)} of{" "}
                {data.now.decisive ?? 0} decisive visits.
              </p>
            ) : null}
            <Chart rows={data.chart} />
          </>
        ) : (
          <p className="text-slate-dim">
            Nothing is being tested at the moment, so your readers are seeing your own ordering
            throughout.
          </p>
        )}
      </section>

      {data.suggestions.length > 0 ? (
        <section className="flex flex-col gap-3">
          <h2 className="text-lg text-slate-normal">Worth adding to this feed</h2>
          {data.suggestions.map((experiment) => (
            <Suggestion key={experiment.id} feedId={id} experiment={experiment} />
          ))}
        </section>
      ) : null}

      <section className="flex flex-col gap-3">
        <h2 className="text-lg text-slate-normal">What your feed has helped settle</h2>
        {data.history.length === 0 ? (
          <p className="text-slate-dim">
            Nothing yet. The first result will appear here once a test finishes.
          </p>
        ) : (
          <div className="flex flex-col">
            {data.history.map((experiment) => (
              <div
                key={experiment.id}
                className="flex flex-col gap-1 py-3 border-b border-slate-5 last:border-b-0"
              >
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="text-slate-normal">{experiment.name}</span>
                  <span className="text-slate-dim text-sm">{experiment.outcome}</span>
                </div>
                <p className="text-slate-dim text-sm">{contribution(experiment)}</p>
              </div>
            ))}
          </div>
        )}
      </section>

      <p className="text-slate-dim text-sm">
        Every result, including this feed&rsquo;s, is public at{" "}
        <Link to="/gym" className="text-iris-11 underline">
          graze.social/gym
        </Link>
        .
      </p>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-col gap-8 p-4 md:p-8 max-w-2xl">{children}</div>;
}
